import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../index";
import {
	AffiliateCoupon,
	AffiliateCreateCouponDTO,
	AffiliateCreateDTO,
	AffiliateMonthlySale,
	AffiliateSale,
	AffiliateSaleDTO,
	AffiliateSalesByMonthDTO,
	AffiliateStatsResponse,
	AffiliateTransfer,
	AffiliateTransferDTO,
	AffiliateUser,
	COUPON_STATUS_LABEL,
	CouponStatusEnum,
	IPayOutType,
	Settlement,
	SettlementDTO
} from "../../services/affiliate/dto/affiliate.dto";
import { AffiliateAPI } from "../../services/affiliate/affiliate.service";
import { adminAPI } from "../../services/admin/admin.service";
interface affiliateState {
	affiliate: AffiliateUser | null;
	affiliateCoupon: AffiliateCoupon | null;
	isFetchingAffiliate: boolean;
	isLoadingAffiliateCoupon: boolean;
	adminAffiliateUsers: AffiliateUser[];
	adminAffiliateUsersTotalItems: number;
	isFetchingAdminAffiliateUsers: boolean;
	isAdminUpdatingAffiliateCoupon: boolean;
	affiliateSales: AffiliateSale[];
	affiliateSalesTotalItems: number;
	affiliateSalesRangeTotalSales: number;
	affiliateSalesRangeTotalCommission: number;
	isFetchingAffiliateSales: boolean;
	settlements: Settlement[];
	settlementsTotalItems: number;
	isFetchingSettlements: boolean;
	affiliateTransfers: AffiliateTransfer[];
	affiliateTransfersTotalItems: number;
	isFetchingAffiliateTransfers: boolean;
	affiliateSalesByMonth: AffiliateMonthlySale[];
	isFetchingAffiliateSalesByMonth: boolean;
	affiliateStats: AffiliateStatsResponse | null;
	isFetchingAffiliateStats: boolean;
}

const initialState: affiliateState = {
	affiliate: null,
	affiliateCoupon: null,
	isFetchingAffiliate: false,
	isLoadingAffiliateCoupon: false,
	adminAffiliateUsers: [],
	adminAffiliateUsersTotalItems: 0,
	isFetchingAdminAffiliateUsers: false,
	isAdminUpdatingAffiliateCoupon: false,
	affiliateSales: [],
	affiliateSalesTotalItems: 0,
	affiliateSalesRangeTotalSales: 0,
	affiliateSalesRangeTotalCommission: 0,
	isFetchingAffiliateSales: false,
	settlements: [],
	settlementsTotalItems: 0,
	isFetchingSettlements: false,
	affiliateTransfers: [],
	affiliateTransfersTotalItems: 0,
	isFetchingAffiliateTransfers: false,
	affiliateSalesByMonth: [],
	isFetchingAffiliateSalesByMonth: false,
	affiliateStats: null,
	isFetchingAffiliateStats: false
};

const mapUserAffiliateResponse = (payload: AffiliateCreateDTO) => {
	const {
		_id,
		business_name,
		payout_method,
		holder_name,
		payout_wallet_number,
		payout_bank_name,
		payout_bank_account_number,
		user_id,
		createdAt
	} = payload;

	const _affiliate: AffiliateUser = {
		_id: _id || "",
		businessName: business_name,
		userName: "",
		firstName: user_id?.firstName || "",
		lastName: user_id?.lastName || "",
		userId: "",
		status: CouponStatusEnum.pending,
		coupon: "sad",
		payoutMethod: {
			type: IPayOutType.BANK,
			holderName: holder_name,
			bankName: payout_bank_name || "",
			accountNumber: payout_bank_account_number || ""
		},
		email: user_id?.email || "",
		createdAt: createdAt || ""
	};

	if (payout_method === 2) {
		_affiliate["payoutMethod"] = {
			type: IPayOutType.WALLET,
			holderName: holder_name,
			walletMobileNumber: payout_wallet_number || ""
		};
	}
	return _affiliate;
};

const mapAffiliateCouponResponse = (payload: AffiliateCreateCouponDTO) => {
	const { affiliate_id, code, status = 0, discount, is_discount_percentage, qr_image } = payload;
	const _coupon: AffiliateCoupon = {
		userId: "",
		affiliateId: affiliate_id,
		code,
		type: 2,
		status: status,
		discount: discount || 0,
		is_discount_percentage: is_discount_percentage || true,
		qr_image: qr_image || ""
	};
	return _coupon;
};

const mapAffiliateSalesResponse = (payload: AffiliateSaleDTO[]): AffiliateSale[] => {
	return payload.map(sale => ({
		id: sale._id || "",
		transactionId: sale.transaction_id.transactionId || "",
		invitationId: sale.transaction_id.invitation_id || "",
		buyerName: sale.buyer_name,
		code: sale.code,
		amount: sale.amount,
		user: sale.user_id,
		currency: sale.currency || "EGP",
		createdAt: sale.createdAt
	}));
};

const mapAffiliateTransfersResponse = (payload: AffiliateTransferDTO[]): AffiliateTransfer[] => {
	return payload.map(transfer => ({
		id: transfer._id || "",
		reference: transfer._id || "",
		month: transfer.month,
		totalSales: transfer.total,
		totalCommission: transfer.total_commission,
		status: transfer.status,
		paidAt: transfer.paid_at
	}));
};

const mapAffiliateSalesByMonthResponse = (payload: AffiliateSalesByMonthDTO[]): AffiliateMonthlySale[] => {
	return payload
		.map(sale => ({ date: sale.date, total: sale.total }))
		.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};

const mapSettlementsResponse = (payload: SettlementDTO[]): Settlement[] => {
	return payload.map(settlement => ({
		id: settlement._id || "",
		firstName: settlement.firstName,
		lastName: settlement.lastName,
		businessName: settlement.business_name,
		totalSales: settlement.totalSales,
		netSales: settlement.netSales,
		status: settlement.status,
		paidAt: settlement.paid_at,
		payoutWalletNumber: settlement.payout_wallet_number,
		payoutBankName: settlement.payout_bank_name,
		payoutBankAccountNumber: settlement.payout_bank_account_number
	}));
};

const mapAdminAffiliateUsers = (payload: AffiliateCreateDTO[]) => {
	return payload.map(affiliate => {
		const _affiliate: AffiliateUser = {
			businessName: affiliate.business_name,
			firstName: affiliate.user_id?.firstName || "",
			lastName: affiliate.user_id?.lastName || "",
			createdAt: affiliate.createdAt,
			email: affiliate.user_id?.email || "",
			coupon: affiliate.coupon?.code || "",
			status: affiliate.coupon?.status || 0,
			userId: affiliate.user_id?._id || ""
		} as AffiliateUser;
		if (affiliate.payout_method === 1) {
			_affiliate["payoutMethod"] = {
				type: IPayOutType.BANK,
				holderName: affiliate.holder_name,
				bankName: affiliate.payout_bank_name || "",
				accountNumber: affiliate.payout_bank_account_number || ""
			};
		} else if (affiliate.payout_method === 2) {
			_affiliate["payoutMethod"] = {
				type: IPayOutType.WALLET,
				holderName: affiliate.holder_name,
				walletMobileNumber: affiliate.payout_wallet_number || ""
			};
		}
		return _affiliate;
	});
};
const affiliateSlice = createSlice({
	name: "affiliate",
	initialState,
	reducers: {},
	extraReducers: builder => {
		builder.addMatcher(AffiliateAPI.endpoints.getUserAffiliate.matchPending, state => {
			state.isFetchingAffiliate = true;
		});
		builder.addMatcher(AffiliateAPI.endpoints.getUserAffiliate.matchFulfilled, (state, action) => {
			state.isFetchingAffiliate = false;
			state.affiliate = mapUserAffiliateResponse(action.payload.affiliate);
		});
		builder.addMatcher(AffiliateAPI.endpoints.getUserAffiliate.matchRejected, state => {
			state.isFetchingAffiliate = false;
		});

		builder.addMatcher(AffiliateAPI.endpoints.getUserAffiliateCoupon.matchPending, state => {
			state.isLoadingAffiliateCoupon = true;
		});
		builder.addMatcher(AffiliateAPI.endpoints.getUserAffiliateCoupon.matchFulfilled, (state, action) => {
			state.isLoadingAffiliateCoupon = false;
			state.affiliateCoupon = mapAffiliateCouponResponse(action.payload.coupon);
		});
		builder.addMatcher(AffiliateAPI.endpoints.getUserAffiliateCoupon.matchRejected, state => {
			state.isLoadingAffiliateCoupon = false;
		});

		builder.addMatcher(AffiliateAPI.endpoints.getAffiliateSales.matchPending, state => {
			state.isFetchingAffiliateSales = true;
		});
		builder.addMatcher(AffiliateAPI.endpoints.getAffiliateSales.matchFulfilled, (state, action) => {
			state.isFetchingAffiliateSales = false;
			state.affiliateSales = mapAffiliateSalesResponse(action.payload.items);
			state.affiliateSalesTotalItems = action.payload.totalItems ?? 0;
			state.affiliateSalesRangeTotalSales = action.payload.totalSales;
			state.affiliateSalesRangeTotalCommission = action.payload.totalCommission;
		});
		builder.addMatcher(AffiliateAPI.endpoints.getAffiliateSales.matchRejected, state => {
			state.isFetchingAffiliateSales = false;
		});

		builder.addMatcher(AffiliateAPI.endpoints.getAffiliateTransfers.matchPending, state => {
			state.isFetchingAffiliateTransfers = true;
		});
		builder.addMatcher(AffiliateAPI.endpoints.getAffiliateTransfers.matchFulfilled, (state, action) => {
			state.isFetchingAffiliateTransfers = false;
			state.affiliateTransfers = mapAffiliateTransfersResponse(action.payload.items);
			state.affiliateTransfersTotalItems = action.payload.totalItems ?? 0;
		});
		builder.addMatcher(AffiliateAPI.endpoints.getAffiliateTransfers.matchRejected, state => {
			state.isFetchingAffiliateTransfers = false;
		});

		builder.addMatcher(AffiliateAPI.endpoints.getAffiliateSalesByMonth.matchPending, state => {
			state.isFetchingAffiliateSalesByMonth = true;
		});
		builder.addMatcher(AffiliateAPI.endpoints.getAffiliateSalesByMonth.matchFulfilled, (state, action) => {
			state.isFetchingAffiliateSalesByMonth = false;
			state.affiliateSalesByMonth = mapAffiliateSalesByMonthResponse(action.payload.sales);
		});
		builder.addMatcher(AffiliateAPI.endpoints.getAffiliateSalesByMonth.matchRejected, state => {
			state.isFetchingAffiliateSalesByMonth = false;
		});

		builder.addMatcher(AffiliateAPI.endpoints.getUserAffiliateStats.matchPending, state => {
			state.isFetchingAffiliateStats = true;
		});
		builder.addMatcher(AffiliateAPI.endpoints.getUserAffiliateStats.matchFulfilled, (state, action) => {
			state.isFetchingAffiliateStats = false;
			state.affiliateStats = action.payload;
		});
		builder.addMatcher(AffiliateAPI.endpoints.getUserAffiliateStats.matchRejected, state => {
			state.isFetchingAffiliateStats = false;
		});

		builder.addMatcher(AffiliateAPI.endpoints.createAffiliateCoupon.matchPending, (state, action) => {
			state.isLoadingAffiliateCoupon = true;
		});
		builder.addMatcher(AffiliateAPI.endpoints.createAffiliateCoupon.matchFulfilled, (state, action) => {
			state.isLoadingAffiliateCoupon = false;
		});
		builder.addMatcher(AffiliateAPI.endpoints.createAffiliateCoupon.matchRejected, (state, action) => {
			state.isLoadingAffiliateCoupon = false;
		});
		builder.addMatcher(adminAPI.endpoints.getAffiliateUsers.matchPending, state => {
			state.isFetchingAdminAffiliateUsers = true;
		});
		builder.addMatcher(adminAPI.endpoints.getAffiliateUsers.matchFulfilled, (state, action) => {
			state.adminAffiliateUsers = mapAdminAffiliateUsers(action.payload.items);
			state.adminAffiliateUsersTotalItems = action.payload.totalItems ?? 0;
			state.isFetchingAdminAffiliateUsers = false;
		});
		builder.addMatcher(adminAPI.endpoints.getAffiliateUsers.matchRejected, state => {
			state.isFetchingAdminAffiliateUsers = false;
		});
		builder.addMatcher(adminAPI.endpoints.updateAffiliateCouponStatus.matchPending, state => {
			state.isAdminUpdatingAffiliateCoupon = true;
		});
		builder.addMatcher(adminAPI.endpoints.updateAffiliateCouponStatus.matchFulfilled, (state, action) => {
			state.isAdminUpdatingAffiliateCoupon = false;
		});
		builder.addMatcher(adminAPI.endpoints.updateAffiliateCouponStatus.matchRejected, state => {
			state.isAdminUpdatingAffiliateCoupon = false;
		});
		builder.addMatcher(adminAPI.endpoints.getSettlements.matchPending, state => {
			state.isFetchingSettlements = true;
		});
		builder.addMatcher(adminAPI.endpoints.getSettlements.matchFulfilled, (state, action) => {
			state.isFetchingSettlements = false;
			state.settlements = mapSettlementsResponse(action.payload.items);
			state.settlementsTotalItems = action.payload.totalItems ?? 0;
		});
		builder.addMatcher(adminAPI.endpoints.getSettlements.matchRejected, state => {
			state.isFetchingSettlements = false;
		});
	}
});

export const getFullAffiliateState = (state: RootState): affiliateState => state.affiliate;
export default affiliateSlice.reducer;
